import { createContext, useContext, useEffect, useState } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';

import type { PanelFeedbackToast, PanelSlowOperation } from './runtime-port';

export type SemanticTone = 'critical' | 'caution' | 'positive' | 'info';

const tonePaths: Readonly<Record<SemanticTone, ReactNode>> = {
  critical: (
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="m7.5 7.5 5 5m0-5-5 5" />
    </>
  ),
  caution: (
    <>
      <path d="M10 3 18 17H2L10 3Z" />
      <path d="M10 8v4m0 2.5v.1" />
    </>
  ),
  positive: (
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="m6.5 10 2.2 2.2 4.8-5" />
    </>
  ),
  info: (
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 9v5m0-8v.1" />
    </>
  ),
};

export function SemanticGlyph({ tone }: { readonly tone: SemanticTone }) {
  return (
    <svg aria-hidden="true" className="state__glyph" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      {tonePaths[tone]}
    </svg>
  );
}

export function StateBadge({ tone, children }: PropsWithChildren<{ readonly tone: SemanticTone }>) {
  return (
    <span className={`state state--${tone}`}>
      <SemanticGlyph tone={tone} />
      <span>{children}</span>
    </span>
  );
}

export interface SystemBannerProps extends PropsWithChildren {
  readonly tone?: Exclude<SemanticTone, 'positive'>;
  readonly urgent?: boolean;
}

export function SystemBanner({ tone = 'info', urgent = false, children }: SystemBannerProps) {
  return (
    <div className={`banner${tone === 'info' ? '' : ` banner--${tone}`}`} role={urgent ? 'alert' : 'status'}>
      <SemanticGlyph tone={tone} />
      <p className="banner__body">{children}</p>
    </div>
  );
}

export function LoadingRegion({ label, rows = 3 }: { readonly label: string; readonly rows?: number }) {
  return (
    <section aria-busy="true" aria-label={label} className="loading-region">
      {Array.from({ length: rows }, (_, index) => (
        <span aria-hidden="true" className="skeleton" key={index} />
      ))}
    </section>
  );
}

/**
 * D-160 ruling 12 — «que sea llamativo, subrayado en rojo».
 *
 * v20 draws values no operation serves (ledger P5). Those render as named
 * absence like any other unknown, and next to the absence this marker names the
 * reason out loud so the owner sees, in the Panel itself, what still needs
 * backend work. The running list is docs/design/panel-v20/PENDING-BACKEND.md.
 *
 * It is a development-build device, never a diner- or staff-facing string in
 * production: Vite inlines the literal, so a build without the flag drops both
 * the element and its text. Read per call, so a preview or a test can decide
 * after the module graph is loaded.
 * Ruling 12 wants the marks on for a plain `npm run dev`, so a development
 * build lights them without the flag; a production build needs the flag. The
 * test mode never lights them by itself, so a spec that asserts their absence
 * stays deterministic and one that wants them sets the flag.
 */
const arePanelDevMarksEnabled = (): boolean =>
  (import.meta.env.DEV && import.meta.env.MODE !== 'test')
  || import.meta.env.VITE_PANEL_DEV_MARKERS === '1';

export function PendingBackendMark() {
  if (!arePanelDevMarksEnabled()) return null;
  return (
    <span aria-label="pendiente de backend" className="pending-backend-mark" role="note">
      pendiente de backend
    </span>
  );
}

/** When a pending change says «Sigue en marcha…» (the PIN gate, and the specs that step its clock). */
export const SLOW_OPERATION_THRESHOLD_MS = 4000;

export function SlowOperationBanner({ operation }: { readonly operation: PanelSlowOperation | null }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    if (operation === null) return undefined;
    const remainingMs = Math.max(0, SLOW_OPERATION_THRESHOLD_MS - (Date.now() - operation.startedAtMs));
    if (remainingMs === 0) {
      setVisible(true);
      return undefined;
    }
    const timeout = window.setTimeout(() => setVisible(true), remainingMs);
    return () => window.clearTimeout(timeout);
  }, [operation]);

  if (!visible || operation === null) return null;
  return (
    <div className="feedback-banner feedback-banner--slow" role="status">
      <span aria-hidden="true" className="feedback-banner__spinner" />
      <span>Sigue en marcha. Está tardando más de lo normal. No cierres esta pantalla.</span>
    </div>
  );
}

export function InactivityWarning({ onStay }: { readonly onStay?: () => void }) {
  return (
    <div className="feedback-banner feedback-banner--idle" role="alert">
      <strong aria-hidden="true">!</strong>
      <span>Vamos a cerrar la sesión por inactividad.</span>
      <button className="btn btn--secondary" onClick={onStay} type="button">
        Sigo aquí
      </button>
    </div>
  );
}

export interface FrozenInteractionBoundaryProps extends PropsWithChildren {
  readonly frozen: boolean;
  /** The session runtime may render its own source-specific status banner. */
  readonly showBanner?: boolean;
  readonly message?: string;
  readonly onResume?: () => void;
}

const FrozenInteractionContext = createContext(false);

/** React context crosses body portals; DOM inert ancestry does not. */
export const useFrozenInteraction = (): boolean => useContext(FrozenInteractionContext);

export function FrozenInteractionBoundary({ frozen, showBanner = true, message, onResume, children }: FrozenInteractionBoundaryProps) {
  const inherited = useFrozenInteraction();
  const blocked = inherited || frozen;
  return (
    <FrozenInteractionContext.Provider value={blocked}>
      <div className="frozen-boundary" data-frozen={blocked}>
        {frozen && showBanner && (
          <div className="banner banner--critical frozen-boundary__banner" role="alert">
            <SemanticGlyph tone="critical" />
            <p className="banner__body">
              <strong>Tablero congelado.</strong>{' '}
              {message ?? 'Lo que ves ya no se actualiza. Las acciones están paradas para evitar cambios sobre datos antiguos.'}
            </p>
            {onResume !== undefined && (
              <button className="btn btn--secondary" onClick={onResume} type="button">
                Reanudar
              </button>
            )}
          </div>
        )}
        <div aria-disabled={blocked || undefined} className="frozen-boundary__content" inert={blocked || undefined}>
          {children}
        </div>
      </div>
    </FrozenInteractionContext.Provider>
  );
}

export interface GlobalFeedbackToastProps {
  readonly toast: PanelFeedbackToast;
  readonly onDismiss?: () => void;
  readonly onRetry?: () => void;
  readonly onUndo?: () => void;
  /** Called by the toast's own action, the button that `toast.actionLabel` names. */
  readonly onAction?: () => void;
  readonly persistent?: boolean;
  readonly testId?: string;
}

export function GlobalFeedbackToast({
  toast,
  onDismiss,
  onRetry,
  onUndo,
  onAction,
  persistent = toast.kind === 'save_failure',
  testId,
}: GlobalFeedbackToastProps) {
  const durationMs = toast.durationMs ?? 3000;

  useEffect(() => {
    if (persistent || onDismiss === undefined) return undefined;
    const timeout = window.setTimeout(onDismiss, durationMs);
    return () => window.clearTimeout(timeout);
  }, [durationMs, onDismiss, persistent, toast.key]);

  return (
    <div
      data-testid={testId}
      aria-live={toast.kind === 'save_failure' ? 'assertive' : 'polite'}
      className={`feedback-toast feedback-toast--${toast.kind}`}
      role={toast.kind === 'save_failure' ? 'alert' : 'status'}
    >
      <SemanticGlyph tone={toast.kind === 'save_failure' ? 'critical' : toast.kind === 'success' ? 'positive' : 'info'} />
      <p className="feedback-toast__message">{toast.message}</p>
      {toast.canUndo && onUndo !== undefined && (
        <button className="btn btn--secondary" onClick={onUndo} type="button">
          Deshacer
        </button>
      )}
      {toast.actionLabel !== undefined && onAction !== undefined && (
        <button className="btn btn--secondary" onClick={onAction} type="button">
          {toast.actionLabel}
        </button>
      )}
      {toast.canRetry && onRetry !== undefined && (
        <button className="btn btn--secondary" onClick={onRetry} type="button">
          Reintentar
        </button>
      )}
      {onDismiss !== undefined && (
        <button aria-label="Cerrar el aviso" className="btn btn--ghost btn--icon" onClick={onDismiss} type="button">
          ×
        </button>
      )}
    </div>
  );
}

